import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  selectSelectedTake3D,
  selectSelectedTakeActors,
} from "../../../app/slices/TrainingSlice";
import PiChipSelect, { PiChipSelectOption } from "../../../components/PiChipSelect";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import EmptyAvatar from "../../../components/actors/EmptyAvatar";
import PropAvatar from "../../../components/actors/PropAvatar";
import usePropHelper from "../../../hooks/PropHelper";
import { Prop } from "../../../models/Prop";
import { noLookatOption } from "./lookatOptions";

export interface PiActorPropChipSelectProps {
  /** An actor id or a prop instance id. */
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
  "aria-label"?: string;
  /** Actor ids left out of the list (props are never filtered). */
  excludeActors?: string[];
  /** Props to offer instead of every prop involved in the take. */
  availableProps?: Prop[];
  /** Adds a "No one" option (value NO_LOOKAT) at the top: the actor stops looking. */
  allowNoActor?: boolean;
}

/**
 * Dropdown over the take's actors and props, grouped under two headings.
 * Used where either can be the target, e.g. who an actor looks at.
 */
export default function PiActorPropChipSelect(props: PiActorPropChipSelectProps) {
  const { value, onChange, excludeActors } = props;
  const { t } = useTranslation();
  const actors = useSelector(selectSelectedTakeActors);
  const take = useSelector(selectSelectedTake3D);
  const { getAllInvolvedProps, getPropDTO } = usePropHelper();

  const actorOptions: PiChipSelectOption[] = actors
    .filter((actor) => actor.id === value || !excludeActors?.includes(actor.id))
    .map((actor) => ({
      id: actor.id,
      label: actor.workingName,
      icon: <ActorAvatar actor={actor} size="28" />,
      group: String(t("chip_select.actors")),
    }));

  const propOptions: PiChipSelectOption[] = (props.availableProps ?? (take ? getAllInvolvedProps(take) : [])).map(
    (prop) => ({
      id: prop.id,
      label: prop.name,
      icon: <PropAvatar src={getPropDTO(prop.propId)?.propContent?.downloadUri} />,
      group: String(t("chip_select.props")),
    })
  );

  return (
    <PiChipSelect
      value={value}
      options={[...(props.allowNoActor ? [noLookatOption(t)] : []), ...actorOptions, ...propOptions]}
      onChange={onChange}
      placeholder={props.placeholder ?? String(t("chip_select.choose_target"))}
      placeholderIcon={<EmptyAvatar />}
      aria-label={props["aria-label"]}
    />
  );
}
